/**
 * What "a favourite" is, in one place.
 *
 * A favourite is a club or a player someone has starred. It exists so the
 * app can answer two questions: which squads to put at the top of the
 * Teams tab, and whose plays to call out during a game.
 *
 * WHY A SUBCOLLECTION AND NOT A PROFILE FIELD. The profile document is
 * validated with `hasOnly` AND `hasAll` on an exact field list, so adding
 * `favouriteTeams` to it would make every profile written before the
 * change fail validation on its next update, until each one was migrated.
 * Favourites live at `users/{uid}/favourites/{favouriteId}` instead, the
 * same shape as `users/{uid}/learning/{conceptId}`, which is additive and
 * needs no migration.
 *
 * The id is derived from the contents rather than generated, so starring
 * the same club twice overwrites rather than duplicating, and so a rule
 * can bind a document to its own id.
 */

export const FAVOURITE_KINDS = Object.freeze({
  TEAM: "team",
  PLAYER: "player",
});

const KINDS = Object.freeze(Object.values(FAVOURITE_KINDS));

/** Ceilings that the Firestore rules repeat. Change one, change both. */
export const FAVOURITE_MAX_REF_LENGTH = 64;

/**
 * How many a person may keep.
 *
 * Not a storage concern: a favourite is a signal about what to surface, and
 * fifty favourite players is the same as none. The cap is enforced in the
 * service rather than in rules, because rules cannot count sibling
 * documents without a read per write.
 */
export const FAVOURITE_LIMITS = Object.freeze({
  [FAVOURITE_KINDS.TEAM]: 5,
  [FAVOURITE_KINDS.PLAYER]: 20,
});

export const isFavouriteKind = (kind) => KINDS.includes(kind);

/**
 * A reference safe to use as half of a document id.
 *
 * Team ids are already short and alphanumeric. Player ids are ours
 * (`nfl-player-4361579`), so they are too, but this refuses anything
 * unexpected rather than letting it reach Firestore and fail there with a
 * less useful message.
 */
export function isValidRef(refId) {
  if (typeof refId !== "string") return false;
  if (refId.length === 0 || refId.length > FAVOURITE_MAX_REF_LENGTH) return false;
  return /^[A-Za-z0-9_-]+$/.test(refId);
}

/** `team__GB`, `player__nfl-player-4361579`. */
export function favouriteId(kind, refId) {
  return `${kind}__${refId}`;
}

/**
 * Build a record, or throw with a reason.
 *
 * Throwing rather than returning null: every caller here is acting on a
 * deliberate tap, so a malformed favourite is a bug worth surfacing, not a
 * condition to swallow.
 */
export function buildFavourite({ kind, refId, addedAt }) {
  if (!isFavouriteKind(kind)) {
    throw new Error(`Unknown favourite kind "${kind}".`);
  }
  if (!isValidRef(refId)) {
    throw new Error(`"${refId}" is not a usable favourite reference.`);
  }
  if (typeof addedAt !== "number" || !Number.isFinite(addedAt)) {
    throw new Error("A favourite needs a real addedAt timestamp.");
  }

  return {
    favouriteId: favouriteId(kind, refId),
    kind,
    refId,
    addedAt,
  };
}

/** Split a list into the two kinds, newest first within each. */
export function groupFavourites(records = []) {
  const byKind = { [FAVOURITE_KINDS.TEAM]: [], [FAVOURITE_KINDS.PLAYER]: [] };

  records.forEach((record) => {
    if (isFavouriteKind(record?.kind)) byKind[record.kind].push(record);
  });

  Object.values(byKind).forEach((list) =>
    list.sort((a, b) => (b.addedAt ?? 0) - (a.addedAt ?? 0))
  );

  return {
    teams: byKind[FAVOURITE_KINDS.TEAM],
    players: byKind[FAVOURITE_KINDS.PLAYER],
  };
}
